import { useContext } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { Divider, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import AppContext from '@/common/context/app.context'
import { focusOnError } from '@/configuration/pages/ConfigurationPage/helpers'
import RegionsSelectedNotSupportedAlert from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/components/RegionsSelectedNotSupportedAlert/RegionsSelectedNotSupportedAlert'

import useShippingMethod from './hooks/useShippingMethod/useShippingMethod'
import ShippingMethodAvailability from './ShippingMethodAvailability/ShippingMethodAvailability'
import ShippingMethodCost from './ShippingMethodCost/ShippingMethodCost'
import ShippingMethodDescription from './ShippingMethodDescription/ShippingMethodDescription'
import ShippingMethodLogistic from './ShippingMethodLogistic/ShippingMethodLogistic'
import ShippingMethodName from './ShippingMethodName/ShippingMethodName'
import ShippingMethodsDetailConfigurationSkeleton from './ShippingMethodsDetailConfigurationSkeleton/ShippingMethodsDetailConfigurationSkeleton'
import ShippingMethodSourceAddress from './ShippingMethodSourceAddress/ShippingMethodSourceAddress'
import { validateShippingMethod } from './helpers'
import * as Styles from './styles'

const ShippingMethodsDetailConfiguration = () => {
  const { id } = useParams()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const location = useLocation()

  const {
    shippingMethod,
    errors,
    setErrors,
    fetchingRegions,
    regions,
    fetchingCurrencies,
    currencies,
    editingShippingMethod,
    creatingShippingMethod,
    carriers,
    fetchingCarriers,
    hasChanges,
    revertChanges,
    onChangeShippingMethod,
    saveChanges,
  } = useShippingMethod({
    id,
    selectedShippingMethod: location.state?.selectedShippingMethod,
    storeId: info.selectedStore.id,
    setToastInfo,
  })

  const onSave = () => {
    const formErrors = validateShippingMethod(shippingMethod, regions)

    if (!formErrors.length) {
      saveChanges(shippingMethod)
    } else {
      focusOnError({ [formErrors[0].type]: formErrors[0].type })
      setErrors(formErrors)
    }
  }

  if (fetchingRegions || fetchingCurrencies || fetchingCarriers) {
    return <ShippingMethodsDetailConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert />}
      <ShippingMethodName
        name={shippingMethod?.name}
        internalName={shippingMethod?.internal_name}
        code={shippingMethod?.code}
        onChange={onChangeShippingMethod}
        errors={errors}
        internalId={shippingMethod?.internal_id}
      />
      <ShippingMethodDescription
        description={shippingMethod?.description}
        onChange={onChangeShippingMethod}
        errors={errors}
      />
      <Divider />
      <ShippingMethodAvailability
        regions={regions || []}
        onChange={onChangeShippingMethod}
        shippingMethodRegions={shippingMethod?.regions || []}
        errors={errors}
        visibility={shippingMethod?.visibility}
        storeId={info.selectedStore.id}
        availabilityCriterionIds={shippingMethod?.availability_criterion_ids}
      />
      <Divider />
      <ShippingMethodLogistic
        carrierSelected={shippingMethod?.carrier?.id}
        onChange={onChangeShippingMethod}
        carriers={carriers}
        deliveryDaysRange={shippingMethod?.delivery_days_range}
        errors={errors}
      />
      <Divider />
      <ShippingMethodSourceAddress
        location={shippingMethod?.location?.id}
        onChange={onChangeShippingMethod}
        errors={errors}
      />
      <Divider />
      <ShippingMethodCost
        currencies={currencies}
        selectedCurrency={shippingMethod?.currency}
        price={shippingMethod?.price}
        onChange={onChangeShippingMethod}
        errors={errors}
        calculateDuties={shippingMethod?.calculate_duties}
        calculateTaxes={shippingMethod?.calculate_taxes}
        calculateHandlingFees={shippingMethod?.calculate_handling_fees}
        dutiesAdjustmentRate={shippingMethod?.duties_adjustment_rate}
      />
      <RegionsSelectedNotSupportedAlert errors={errors} />
      <SaveChangesButtons
        loading={editingShippingMethod || creatingShippingMethod}
        disabled={editingShippingMethod || creatingShippingMethod}
        onSave={() => onSave()}
        hasChanges={hasChanges}
        onRevertChanges={revertChanges}
      />
    </Styles.Container>
  )
}

export default ShippingMethodsDetailConfiguration
